import type { CensusPointInTimes } from "../../../utils/models";

/**
 * Rendering of the CDPH §72329.2 census readings, shared by the summary plate and the detailed sheet
 * header so the two printouts can never label or round the same numbers differently.
 *
 * Labels are hardcoded English, matching every other string on these sheets ("Census", "Nursing
 * Hours", "HPPD") — the printout is not translated, and a half-translated sheet reads worse than an
 * English one.
 */

export const AVERAGE_LABEL = "Average Census (CDPH)";

/**
 * Facility-local hour → the label the sheet shows for that reading, in 12-hour form for all 24 hours.
 *
 * Reading hours are department configuration since DAT-717, so this has to cover every hour rather
 * than the three CDPH ones: the old map fell back to `Census 14:00`, which would print in 24-hour
 * form directly beside a `Census 4:00 PM` from the map. The three CDPH strings are byte-identical to
 * what that map produced, so shipped California sheets are unchanged.
 */
const hourLabel = (hour: number): string => {
	const meridiem = hour < 12 ? "AM" : "PM";
	const hour12 = hour % 12 === 0 ? 12 : hour % 12;
	return `Census ${hour12}:00 ${meridiem}`;
};

/** A reading's value formatted for a cell — whole numbers stay whole, the average keeps its decimals. */
export const formatCensus = (value: number): number => (Number.isInteger(value) ? value : Number(value.toFixed(2)));

/**
 * The rows for the summary printout's census plate: `[label, value]` per reading, then the average.
 *
 * Empty when there is nothing to show — feature off, non-California department, no ADT-capable EMR, or
 * a day whose first reading hasn't happened yet (a future date). Callers render nothing at all in that
 * case rather than a heading with no numbers under it.
 */
export const censusPointInTimeRows = (censusPointInTimes: CensusPointInTimes | undefined): { label: string; value: number }[] => {
	if (!censusPointInTimes?.available || censusPointInTimes.points.length === 0) return [];

	const rows = censusPointInTimes.points.map((point) => ({ label: hourLabel(point.hour), value: formatCensus(point.census) }));
	if (censusPointInTimes.average !== null) rows.push({ label: AVERAGE_LABEL, value: formatCensus(censusPointInTimes.average) });
	return rows;
};

/**
 * The same readings as single "label: value" strings, for the detailed sheet header where they share
 * one row instead of occupying a two-column plate.
 */
export const censusPointInTimeCells = (censusPointInTimes: CensusPointInTimes | undefined): string[] =>
	censusPointInTimeRows(censusPointInTimes).map((row) => `${row.label}: ${row.value}`);
